import { ReactNode } from 'react';
import { Route, Switch } from 'react-router-dom';

import { CheckoutStep } from './Checkout/helpers';
import { useEvent } from './Common/EventProvider';
import AboutPage from './About/AboutPage';
import BuyRegistrationPage from './Resale/BuyRegistrationPage';
import CheckoutPage from './Checkout/CheckoutPage';
import CheckoutProvider from './Checkout/CheckoutProvider';
import CheckoutSummaryProvider, { useCheckoutSummary } from './Checkout/CheckoutSummaryProvider';
import ErrorBoundary from './Checkout/ErrorBoundary';
import EventNotFound from '../common/EventNotFound';
import InterestPage from './InterestList/InterestPage';
import Layout from './Common/Layout';
import NetworkError from '../common/NetworkError';
import OverviewPage from './Resale/OverviewPage';
import PreregistrationPage from './Checkout/PreregistrationPage';
import PrivacyPage from './Privacy/PrivacyPage';
import SuccessPage from './Checkout/Success/SuccessPage';
import UI from '../common/UI';
import WaitlistPage from './Waitlist/WaitlistPage';
import WidgetContainer from './Common/WidgetContainer';

const Main = () => {
  const { event, error } = useEvent();

  const eventNotFoundError = !!(error?.graphQLErrors?.[0]?.extensions?.validation as any)?.event?.exists;
  const serverError = !!error && !eventNotFoundError;

  return (
    <ErrorBoundary redirect={event?.checkout_url}>
      <WidgetContainer showRegisterButton={!!event}>
        {eventNotFoundError && <EventNotFound />}
        {serverError && <NetworkError />}

        {!eventNotFoundError && !serverError && (
          <Switch>
            <Route
              path="/:eventId/waitlist/:ticketId"
              render={({ match: { params: { ticketId } } }) => (
                <EventLoader focusMode>
                  <WaitlistPage ticketId={ticketId} />
                </EventLoader>
              )}
            />
            <Route
              path="/:eventId/preregistration/:ticketId/:promotionId"
              render={({ match: { params: { ticketId, promotionId } } }) => (
                <EventLoader focusMode>
                  <PreregistrationPage
                    ticketId={ticketId}
                    promotionId={promotionId}
                  />
                </EventLoader>
              )}
            />
            <Route path="/:eventId/interest">
              <EventLoader>
                <InterestPage />
              </EventLoader>
            </Route>
            <Route
              path="/:eventId/resale/:registrationId/:resaleToken"
              render={({ match: { params: { registrationId, resaleToken } } }) => (
                <EventLoader focusMode>
                  <BuyRegistrationPage
                    registrationId={registrationId}
                    resaleToken={resaleToken}
                  />
                </EventLoader>
              )}
            />
            <Route path="/:eventId/resale">
              <EventLoader>
                <OverviewPage />
              </EventLoader>
            </Route>
            <Route path="/:eventId/privacy">
              <EventLoader focusMode>
                <PrivacyPage />
              </EventLoader>
            </Route>
            <Route
              path="/:eventId/:step"
              render={({ match: { params: { step } } }) => (
                <CheckoutSummaryProvider>
                  <CheckoutLoader focusMode={step && step !== CheckoutStep.Tickets}>
                    <CheckoutProvider
                      step={step as any}
                      render={(props) => (
                        <Switch>
                          <Route path="/:eventId/thank-you">
                            <SuccessPage />
                          </Route>
                          <Route>
                            <CheckoutPage {...props} />
                          </Route>
                        </Switch>
                      )}
                    />
                  </CheckoutLoader>
                </CheckoutSummaryProvider>
              )}
            />
            <Route path="/:eventId">
              <EventLoader>
                <AboutPage />
              </EventLoader>
            </Route>
          </Switch>
        )}
      </WidgetContainer>
    </ErrorBoundary>
  );
};

interface EventLoaderProps {
  focusMode?: boolean;
  children?: ReactNode;
}

export const EventLoader = ({ focusMode, children }: EventLoaderProps) => {
  const { event } = useEvent();

  if (!event) {
    return <UI.PageLoader data-testid="event-loader" />;
  }

  return (
    <Layout focusMode={focusMode}>
      {children}
    </Layout>
  );
};

interface CheckoutLoaderProps {
  focusMode?: boolean;
  children?: ReactNode;
}

export const CheckoutLoader = ({ focusMode, children }: CheckoutLoaderProps) => {
  const { event } = useEvent();
  const { checkoutSummary } = useCheckoutSummary();

  if (!event) {
    return <UI.PageLoader data-testid="event-loader" />;
  }

  return (
    <Layout focusMode={focusMode}>
      {checkoutSummary
        ? children
        : (
          <UI.Card sc={{ background: 'gray.150' }}>
            <UI.PageLoader height={250} data-testid="checkout-summary-loader" />
          </UI.Card>
        )}
    </Layout>
  );
};

export default Main;
